import type { Route } from './+types/local-links';
import type { ImageCardItem } from '@/components/sections/image-card';
import type { ResourceItem } from '@/components/sections/resource-list';
import { CtaBanner } from '@/components/sections/cta-banner';
import { HeroSection } from '@/components/sections/hero-section';
import { ImageCardGrid } from '@/components/sections/image-card';
import { ResourceListSection } from '@/components/sections/resource-list';
import { inheritRootLdJson } from '@/utils/schema';

const CULTURE_EVENTS: Array<ImageCardItem> = [
  {
    id: 'kingaroy-baconfest',
    image: '/static/local/kingaroy-baconfest.png',
    imageAlt: 'Kingaroy BaconFest logo',
    eyebrow: 'Food & Festivals',
    title: 'Kingaroy BaconFest',
    description:
      "Kingaroy's biggest food celebration, held at Memorial Park with the Big Bacon Breakfast, a Bacon Street Feast, celebrity chef cooking demonstrations, live music, and a much-loved bacon-eating competition. The festival now runs biennially — the next BaconFest is set for 20–21 August 2027.",
    href: 'https://www.kingaroybaconfest.com.au/',
    linkLabel: 'Visit the BaconFest website',
  },
  {
    id: 'chrissy-trees-4-u',
    image: '/static/local/chrissy-trees-4-u.webp',
    imageAlt: 'Rows of pine Christmas trees at the Chrissy Trees 4 U farm near Kingaroy',
    eyebrow: 'Local Producers',
    title: 'Chrissy Trees 4 U',
    description:
      "A family-run tree farm just outside Kingaroy, growing and selling real, fragrant pine Christmas trees fully hardened to the warm Queensland summer — a favourite local tradition each December.",
    href: 'https://www.chrissytrees4u.com.au/',
  },
];

const COUNCIL_RESOURCES: Array<ResourceItem> = [
  {
    id: 'sbrc',
    title: 'South Burnett Regional Council (SBRC)',
    href: 'https://www.southburnett.qld.gov.au',
    hrefLabel: 'southburnett.qld.gov.au',
    contact: [
      { label: 'phone', value: '07 4189 9100', href: 'tel:0741899100' },
      { label: 'email', value: 'info@southburnett.qld.gov.au', href: 'mailto:info@southburnett.qld.gov.au' },
      { label: 'address', value: '45 Glendon Street, Kingaroy' },
    ],
    description:
      'The main council website covering rates, bins, roads, local events, and all council services for the South Burnett region.',
  },
  {
    id: 'sbrc-planning-development',
    title: 'SBRC — Planning & Development',
    href: 'https://www.southburnett.qld.gov.au/planning-development',
    hrefLabel: 'southburnett.qld.gov.au/planning-development',
    description:
      'Covers planning scheme provisions, zoning, development application searches, infrastructure charges, development approvals, and the local heritage register. Essential for buyers or developers doing due diligence.',
  },
  {
    id: 'sbrc-planning-searches',
    title: 'SBRC — Planning Searches (Property Zoning Lookup)',
    href: 'https://www.southburnett.qld.gov.au/Business/Building-and-development/Planning-and-development/Planning-searches',
    hrefLabel: 'southburnett.qld.gov.au — Planning Searches',
    description:
      'Search a specific property to obtain information about zonings, planning scheme provisions, infrastructure charges, and development approvals.',
  },
  {
    id: 'sbrc-contact-councillor',
    title: 'SBRC — Contact Your Councillor',
    href: 'https://www.southburnett.com.au/sbrc',
    hrefLabel: 'southburnett.com.au/sbrc',
    description: 'Lists all divisional councillors with direct phone numbers and emails, including the Mayor.',
  },
];

const QLD_GOVERNMENT_RESOURCES: Array<ResourceItem> = [
  {
    id: 'qro-transfer-duty',
    title: 'Queensland Revenue Office — Transfer Duty (Stamp Duty)',
    href: 'https://www.qld.gov.au/housing/buying-owning-home/home-buyers-financial-help/transfer-duty',
    hrefLabel: 'qld.gov.au — Transfer Duty',
    description:
      'Explains transfer duty (stamp duty) in Queensland, including available concessions and exemptions for first home buyers, owner-occupiers, and others.',
  },
  {
    id: 'qro-first-home-concession',
    title: 'Queensland Revenue Office — First Home (New Home) Concession',
    href: 'https://qro.qld.gov.au/duties/transfer-duty/concessions/homes/first-home-new-home',
    hrefLabel: 'qro.qld.gov.au — First Home (New Home) Concession',
    description:
      'First home buyers purchasing a new home may be eligible for a full transfer duty concession, reducing duty to nil. Contracts must be dated 1 May 2025 or later and there is no value cap on the home itself.',
  },
  {
    id: 'first-home-concession-overview',
    title: 'Queensland Government — First Home Stamp Duty Concession Overview',
    href: 'https://aplacetocallhome.initiatives.qld.gov.au/initiatives/stamp-duty-concession',
    hrefLabel: 'aplacetocallhome.initiatives.qld.gov.au',
    description:
      'A plain-English overview of the full stamp duty concession available to first home buyers purchasing a new home or vacant land to build on.',
  },
];

const PROPERTY_MARKET_RESOURCES: Array<ResourceItem> = [
  {
    id: 'ips-kingaroy-suburb-report',
    title: 'Kingaroy Suburb Report (IPS Buyers Agents)',
    description:
      "A detailed report on Kingaroy's property market, noting a median house price of $473,000 and annual growth of 23.2%, with analysis of rental yields, infrastructure investment, and lifestyle appeal. Useful for conversations with potential investors.",
  },
];

const TOURISM_RESOURCES: Array<ResourceItem> = [
  {
    id: 'visit-south-burnett-kingaroy',
    title: 'Visit South Burnett — Kingaroy',
    href: 'https://visitsouthburnett.com.au/kingaroy/',
    hrefLabel: 'visitsouthburnett.com.au/kingaroy',
    description:
      'The official tourism guide to Kingaroy, covering attractions including the Heritage Museum, Regional Art Gallery, Visitor Information Centre, Mt Wooroolin lookout, the Kingaroy Observatory, and the South Burnett wine trail. Handy to share with people relocating or considering the area.',
  },
  {
    id: 'kingaroy-visitor-information-centre',
    title: 'Kingaroy Visitor Information Centre',
    href: 'https://www.southburnett.qld.gov.au/visitor-information-centres/visitor-information-centres-1/1',
    hrefLabel: 'southburnett.qld.gov.au — Visitor Information',
    contact: [
      { label: 'address', value: '128 Haly Street, Kingaroy' },
      { label: 'phone', value: '07 4189 9555', href: 'tel:0741899555' },
      { label: 'email', value: 'tourism@sbrc.qld.gov.au', href: 'mailto:tourism@sbrc.qld.gov.au' },
    ],
    description:
      'Located in the Kingaroy Information Art and Heritage Precinct opposite the peanut silos. Open Monday–Friday 9am–4pm, Saturday 9am–1pm. Staffed by locals and stocks maps, brochures, and locally made products.',
  },
  {
    id: 'discover-south-burnett-kingaroy',
    title: 'Discover South Burnett — Kingaroy Town Guide',
    href: 'https://www.discoversouthburnett.com.au/town/kingaroy/',
    hrefLabel: 'discoversouthburnett.com.au/town/kingaroy',
    description:
      'A community-run tourism directory listing Kingaroy accommodation, dining, attractions, and local events. Good for newcomers researching the area.',
  },
  {
    id: 'kingaroy-community-info-hub',
    title: 'Kingaroy Community Info Hub',
    href: 'https://mycommunityinfo.com.au/QLD/UCL/Kingaroy',
    hrefLabel: 'mycommunityinfo.com.au/QLD/UCL/Kingaroy',
    description:
      'Lists key emergency contacts (SBRC, SES, Lifeline) alongside local services, clubs, outreach programmes, and community events for Kingaroy.',
  },
];

const HEALTH_RESOURCES: Array<ResourceItem> = [
  {
    id: 'kingaroy-hospital',
    title: 'Kingaroy Hospital',
    href: 'https://www.darlingdowns.health.qld.gov.au/hospitals-and-health-centres/kingaroy-hospital',
    hrefLabel: 'darlingdowns.health.qld.gov.au — Kingaroy Hospital',
    contact: [{ label: 'phone', value: '07 4162 0200', href: 'tel:0741620200' }],
    description:
      'The main healthcare hub for the South Burnett region, providing general medicine, surgery, obstetrics, aged care, dental, physiotherapy, and speech pathology services.',
  },
];

const EDUCATION_RESOURCES: Array<ResourceItem> = [
  {
    id: 'kingaroy-state-school',
    title: 'Kingaroy State School',
    href: 'https://kingaroyss.eq.edu.au/',
    hrefLabel: 'kingaroyss.eq.edu.au',
    description:
      'The official Queensland Department of Education site for the local primary school. Useful for families with children moving to the area.',
  },
  {
    id: 'kingaroy-state-high-school',
    title: 'Kingaroy State High School',
    href: 'https://schoolsdirectory.eq.edu.au/Details/2044',
    hrefLabel: 'schoolsdirectory.eq.edu.au — Kingaroy SHS',
    contact: [{ label: 'email', value: 'admin@kingaroyshs.eq.edu.au', href: 'mailto:admin@kingaroyshs.eq.edu.au' }],
    description:
      'A co-educational high school offering Year 7–12 with both academic and vocational pathways, plus extracurricular activities including music, drama, and sport.',
  },
  {
    id: 'st-marys-college-kingaroy',
    title: "St Mary's College Kingaroy",
    description: 'The local Catholic P–12 school — searchable via the SBRC My Community Directory below.',
  },
];

const NEWS_COMMUNITY_RESOURCES: Array<ResourceItem> = [
  {
    id: 'southburnett-com-au',
    title: 'southburnett.com.au — Local Online Newspaper',
    href: 'https://southburnett.com.au/',
    hrefLabel: 'southburnett.com.au',
    description:
      'A locally owned, no-paywall online newspaper covering the South Burnett region — council decisions, Kingaroy property news, local sport, events, and community stories. The most-read local news source for the region.',
  },
  {
    id: 'sbrc-my-community-directory',
    title: 'SBRC — My Community Directory',
    href: 'https://www.southburnett.qld.gov.au/Living-Here/Community-Information/My-Community-Directory',
    hrefLabel: 'southburnett.qld.gov.au — My Community Directory',
    description:
      'A Council-partnered directory providing up-to-date information on community organisations, services, clubs, and events across the South Burnett region.',
  },
];

export function meta({ matches }: Route.MetaArgs) {
  return [
    ...inheritRootLdJson(matches),
    { title: 'Local Links & Resources | Kingaroy & South Burnett | Meet Real Estate' },
    {
      name: 'description',
      content:
        'A handy directory of council, government, tourism, health, education, news and property market resources for Kingaroy and the South Burnett — useful for residents, newcomers and anyone considering a move to the region.',
    },
  ];
}

export default function LocalLinks() {
  return (
    <main>
      <HeroSection
        eyebrow="Local Resources"
        heading="Kingaroy & South Burnett — Local Links & Resources"
        description="A handy directory of council, government, tourism, health, education and news resources for the South Burnett region — useful for residents, newcomers and anyone considering a move to the area."
        primaryCta={{ label: 'Get in Touch', to: '#contact' }}
      />

      <ResourceListSection
        id="council"
        className="scroll-mt-20"
        eyebrow="Council & Local Government"
        heading="South Burnett Regional Council"
        resources={COUNCIL_RESOURCES}
      />

      <ResourceListSection
        id="government-finance"
        className="section--navy-mid scroll-mt-20"
        eyebrow="Queensland Government"
        heading="Property & Finance"
        resources={QLD_GOVERNMENT_RESOURCES}
      />

      <ResourceListSection
        id="market-data"
        className="scroll-mt-20"
        eyebrow="Property Market Data"
        heading="Kingaroy Market Insights"
        resources={PROPERTY_MARKET_RESOURCES}
      />

      <ResourceListSection
        id="tourism-community"
        className="section--navy-mid scroll-mt-20"
        eyebrow="Tourism & Community"
        heading="Discovering Kingaroy"
        resources={TOURISM_RESOURCES}
      />

      <ImageCardGrid
        id="culture-events"
        className="scroll-mt-20"
        eyebrow="Culture & Events"
        heading="Local Culture & Events"
        description="Beyond the market data — the festivals and local producers that give Kingaroy its character."
        items={CULTURE_EVENTS}
      />

      <ResourceListSection
        id="health"
        className="section--navy-mid scroll-mt-20"
        eyebrow="Health"
        heading="Healthcare in Kingaroy"
        resources={HEALTH_RESOURCES}
      />

      <ResourceListSection
        id="education"
        className="scroll-mt-20"
        eyebrow="Education"
        heading="Schools in Kingaroy"
        resources={EDUCATION_RESOURCES}
      />

      <ResourceListSection
        id="news-community"
        className="section--navy-mid scroll-mt-20"
        eyebrow="Local News & Community"
        heading="Staying Informed"
        resources={NEWS_COMMUNITY_RESOURCES}
      />

      <CtaBanner
        id="contact"
        className="scroll-mt-20"
        heading="Thinking About Buying or Selling in the South Burnett?"
        description="Whether you're new to the area or a long-time local, a confidential conversation costs you nothing."
        primaryCta={{ label: 'Request an appraisal', to: '/appraisal' }}
        secondaryCta={{ label: 'Call Lucio', to: 'tel:0493035628' }}
        contactName="Lucio Passos"
        contactTitle="Sales Agent, Meet Real Estate"
        contactLinks={[
          { label: 'Mobile', value: '0493 035 628', href: 'tel:0493035628' },
          { label: 'Office', value: '07 3355 7777', href: 'tel:0733557777' },
          { label: 'Email', value: 'lucio@meetrealestate.com.au', href: 'mailto:lucio@meetrealestate.com.au' },
          { label: 'Website', value: 'meetrealestate.com.au', href: 'https://meetrealestate.com.au' },
        ]}
      />
    </main>
  );
}
